import { IconButton, Tooltip } from '@material-ui/core';
import { Add, Lock, Search } from '@material-ui/icons';
import { Inline, useResponsiveValue } from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { useCallback, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { shouldIgnoreKeyboardShortcut } from 'shared/helpers/KeyboardShortcutHelpers';
import { FilterIcon } from 'shared/icons/FilterIcon';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { KeyboardShortcutBadge } from 'shared/ui/KeyboardShortcutBadge';
import styled from 'styled-components';
import { formatLoadStage } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { findLoadsPageParamsSearchEntry } from '../data/LoadsPageParams';
import { useLoadsPageContext } from './data/LoadsPageParamsContext';
import { LoadsAddLoadDialog } from './LoadsAddLoadDialog';
import { LoadsImportButton } from './LoadsImportButton';
import { LoadsPageFilterSearch } from './LoadsPageFilterSearch';
import { LoadsPageFilterMobile } from './mobile/LoadsPageFilterMobile';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { useNavigate } from 'react-router-dom';

const StyledBox = styled(Box)<{
  flexDirection?: React.CSSProperties['flexDirection'];
}>`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-direction: ${({ flexDirection }) => flexDirection};
`;
const StyledIconButton = styled(IconButton)`
  &[data-has-badge='true']::before {
    content: ' ';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 16px;
    height: 16px;
    background-color: red;
    border-radius: 50%;
    border: 2px solid white;
  }
`;
interface LoadsPageHeaderActionsProps {
  onUpgradePlan: () => void;
  onImportedLoads: () => void;
}

export function LoadsPageHeaderActions({
  onUpgradePlan,
  onImportedLoads,
}: LoadsPageHeaderActionsProps) {
  const navigate = useNavigate();
  const isMobile = useResponsiveValue(true, false);
  const isAddLoadFlowEnabled = useFlag('ctms_loads_add_load');
  const { data: settings } = useCarrierSettings();
  const shouldRestrictCreateLoad = !settings?.can_create_load;
  const hasNoCreateLoadAccess = usePermissionAccess({
    name: 'loads',
    permission: 'can_create',
  });
  const { params, updateParams, isEmptyFilterParams } = useLoadsPageContext();
  const [modalState, setModalState] = useState<'filters' | 'search'>();
  const [isAddLoadDialogOpen, setIsAddLoadDialogOpen] = useState(false);
  const [searchName, searchValue] = useMemo(
    () => findLoadsPageParamsSearchEntry(params),
    [params],
  );

  const addLoadButtonLabel = isAddLoadFlowEnabled ? (
    <Inline verticalAlign="center">
      Add Load
      <Inline space="xxsmall">
        <KeyboardShortcutBadge>C</KeyboardShortcutBadge>
      </Inline>
    </Inline>
  ) : (
    'Create Load'
  );
  const renderAddLoadButtonContent = isMobile ? <Add /> : addLoadButtonLabel;

  const openAddLoadDialog = useCallback((isKeyboardShortcut: boolean) => {
    trackLoadsEvent({
      name: 'Carrier Opened Add Load Dialog',
      is_keyboard_shortcut: isKeyboardShortcut,
    });
    setIsAddLoadDialogOpen(true);
  }, []);

  const handleAddLoadClick = useCallback(() => {
    trackLoadsEvent({
      name: 'Carrier Clicked Create Load',
      utm_medium: formatLoadStage(params.stage),
    });

    if (isAddLoadFlowEnabled) {
      openAddLoadDialog(false);
      return;
    }

    navigate('/loads/add');
  }, [isAddLoadFlowEnabled, navigate, openAddLoadDialog, params.stage]);

  const handleAddLoadKeyboardShortcut = useCallback(() => {
    openAddLoadDialog(true);
  }, [openAddLoadDialog]);

  useHotkeys(
    'c',
    handleAddLoadKeyboardShortcut,
    {
      enabled:
        isAddLoadFlowEnabled &&
        !shouldRestrictCreateLoad &&
        !hasNoCreateLoadAccess,
      preventDefault: true,
      ignoreEventWhen: shouldIgnoreKeyboardShortcut,
    },
    [
      handleAddLoadKeyboardShortcut,
      hasNoCreateLoadAccess,
      isAddLoadFlowEnabled,
      shouldRestrictCreateLoad,
    ],
  );

  return (
    <div data-intercom-target="loads-page-header-actions">
      <LoadsAddLoadDialog
        open={isAddLoadFlowEnabled && isAddLoadDialogOpen}
        onClose={() => {
          setIsAddLoadDialogOpen(false);
        }}
      />

      {isMobile && (
        <>
          <LoadsPageFilterMobile
            open={modalState === 'filters'}
            onClose={() => {
              setModalState(undefined);
            }}
          />

          <LoadsPageFilterSearch
            drawerProps={{
              open: modalState === 'search',
              onClose: () => {
                setModalState(undefined);
              },
            }}
            name={searchName}
            value={searchValue}
            onSearch={(field, value) => {
              updateParams(
                { stage: 'active', [field]: value },
                { strategy: 'reset' },
              );
              trackLoadsEvent({ name: 'CTMS: Searched Loads', field, value });
            }}
          />
        </>
      )}
      <StyledBox flexDirection={isMobile ? 'row-reverse' : 'row'}>
        {shouldRestrictCreateLoad ? (
          <Tooltip
            placement="bottom"
            title={
              <Inline verticalAlign="center">
                <Lock />
                <TextBox variant="body" color="white">
                  Upgrade to unlock this feature.
                </TextBox>
              </Inline>
            }
          >
            <Button
              startIcon={<Lock />}
              variant="default"
              onClick={() => {
                onUpgradePlan();
                trackLoadsEvent({
                  name: 'Carrier Clicked Create Load',
                  utm_medium: formatLoadStage(params.stage),
                });
              }}
            >
              {renderAddLoadButtonContent}
            </Button>
          </Tooltip>
        ) : (
          <PermissionButton
            name="loads"
            permission="can_create"
            variant="default"
            startIcon={isAddLoadFlowEnabled && <Add />}
            onClick={handleAddLoadClick}
          >
            {!isMobile && addLoadButtonLabel}
          </PermissionButton>
        )}

        {!isAddLoadFlowEnabled && (
          <LoadsImportButton
            onSubmitSuccess={() => {
              onImportedLoads();
            }}
            onClick={() => {
              onUpgradePlan();
            }}
          />
        )}

        {isMobile && (
          <>
            {params.stage !== 'active' && (
              <StyledIconButton
                data-has-badge={!isEmptyFilterParams || !!params.order_by}
                size="small"
                onClick={() => {
                  setModalState('filters');
                }}
              >
                <FilterIcon color="action" />
              </StyledIconButton>
            )}

            <IconButton
              size="small"
              onClick={() => {
                setModalState('search');
              }}
            >
              <Search color="action" fontSize="small" />
            </IconButton>
          </>
        )}
      </StyledBox>
    </div>
  );
}
